import { useMemo } from "react";
import { CacheVerdictChip } from "@/components/CacheChip";
import {
  cacheDetail,
  type CacheFields,
  type ColdUnitRow,
} from "@/lib/cacheVerdict";
import { fmtSpanMs, parseMemTelemetry } from "@/lib/memTelemetry";

const WHY_HINT =
  "No reason recorded. Set PURE_RUNNER_BUILD_DIAG=1 in the runner's environment to have cargo explain why each unit was dirty.";

function UnitName({ u }: { u: ColdUnitRow }) {
  return (
    <span className="font-mono">
      {u.name}
      {u.version ? (
        <span className="text-muted-foreground"> {u.version}</span>
      ) : null}
      {u.buildScript ? (
        <span className="text-muted-foreground"> (build script)</span>
      ) : null}
    </span>
  );
}

/** "Build cache" section of the job detail page: warm/cold verdict, the cold
 *  cargo units split into expected (the program's own crates and wrapper) and
 *  unexpected (cache misses) with versions and, when the runner had
 *  PURE_RUNNER_BUILD_DIAG on, why cargo called each dirty; plus cargo time. */
export function CachePanel({
  row,
}: {
  row: CacheFields & {
    status?: string;
    mem_telemetry?: string | null;
    build_kind?: string | null;
    pgo_generate?: number | null;
    pgo_profile_path?: string | null;
  };
}) {
  const detail = useMemo(
    () => cacheDetail(parseMemTelemetry(row.mem_telemetry), row),
    [row],
  );
  const active = row.status === "queued" || row.status === "running";
  if (active && detail.verdict.kind === "unknown") return null;
  const { verdict } = detail;
  return (
    <div className="space-y-2" data-testid="cache-panel">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold">Build cache</h3>
        <CacheVerdictChip verdict={verdict} />
        {detail.compiled != null && (
          <span className="text-xs tabular-nums text-muted-foreground">
            {detail.compiled} cargo unit{detail.compiled === 1 ? "" : "s"}{" "}
            compiled
            {detail.fresh != null
              ? `, ${detail.fresh} reused from the warm cache`
              : ""}
          </span>
        )}
        {detail.cargoMs != null && (
          <span
            className="text-xs tabular-nums text-muted-foreground"
            data-testid="cache-cargo-time"
          >
            cargo took {fmtSpanMs(detail.cargoMs)}
          </span>
        )}
      </div>
      <p className="whitespace-pre-line text-xs text-muted-foreground">
        {verdict.title}
      </p>
      {detail.unexpected.length > 0 && (
        <div className="space-y-1">
          <div className="text-xs font-semibold">
            Unexpected cold units ({detail.unexpected.length}
            {detail.unlisted > 0
              ? ` listed, ${detail.unlisted} more not listed`
              : ""}
            )
          </div>
          <table className="w-full text-xs" data-testid="cache-unexpected">
            <thead>
              <tr className="text-left text-muted-foreground">
                <th className="pr-3 py-0.5 font-medium">Crate</th>
                <th className="py-0.5 font-medium">Why cargo rebuilt it</th>
              </tr>
            </thead>
            <tbody>
              {detail.unexpected.map((u, i) => (
                <tr
                  key={`${u.name}:${u.buildScript}:${i}`}
                  className="align-top"
                >
                  <td className="pr-3 py-0.5">
                    <UnitName u={u} />
                  </td>
                  <td className="py-0.5 font-mono">
                    {u.reasons.length > 0 ? (
                      u.reasons.join("; ")
                    ) : (
                      <span className="text-muted-foreground" title={WHY_HINT}>
                        —
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {detail.expected.length > 0 && (
        <p className="text-xs" data-testid="cache-expected">
          <span className="font-semibold">Expected cold</span>{" "}
          <span className="text-muted-foreground">
            (the program's own crates and wrapper):
          </span>{" "}
          {detail.expected.map((u, i) => (
            <span key={`${u.name}:${u.buildScript}:${i}`}>
              {i > 0 ? ", " : ""}
              <UnitName u={u} />
            </span>
          ))}
        </p>
      )}
    </div>
  );
}
